import Layout from "@/components/layouts/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { PLATFORM_ROUTES } from "@/lib/platform";
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  CheckCircle,
  GraduationCap,
  Map,
  MessagesSquare,
  MessageSquareText,
  Rss,
  Sparkles,
  UserCircle,
  Users,
  Wrench,
} from "lucide-react";

const featureModules = [
  { key: "profiles", icon: UserCircle, color: "text-nexus-teal", bg: "bg-nexus-teal/10" },
  { key: "feed", icon: Rss, color: "text-nexus-yellow", bg: "bg-nexus-yellow/10" },
  { key: "discussions", icon: MessageSquareText, color: "text-nexus-red", bg: "bg-nexus-red/10" },
  { key: "connections", icon: MessagesSquare, color: "text-purple-600", bg: "bg-purple-100 dark:bg-purple-950/40" },
  { key: "mentorship", icon: GraduationCap, color: "text-nexus-teal", bg: "bg-nexus-teal/10" },
  { key: "groups", icon: Users, color: "text-nexus-yellow", bg: "bg-nexus-yellow/10" },
  { key: "roadmaps", icon: Map, color: "text-nexus-red", bg: "bg-nexus-red/10" },
  { key: "tools", icon: Wrench, color: "text-purple-600", bg: "bg-purple-100 dark:bg-purple-950/40" },
  { key: "events", icon: CalendarDays, color: "text-nexus-teal", bg: "bg-nexus-teal/10" },
  { key: "jobs", icon: Briefcase, color: "text-nexus-yellow", bg: "bg-nexus-yellow/10" },
] as const;

const deepDives = [
  { key: "profiles", icon: UserCircle, href: PLATFORM_ROUTES.signup },
  { key: "networking", icon: Rss, href: PLATFORM_ROUTES.feeds },
  { key: "learning", icon: GraduationCap, href: PLATFORM_ROUTES.roadmaps },
  { key: "opportunities", icon: Briefcase, href: PLATFORM_ROUTES.community },
] as const;

const stats = [
  { key: "members", value: "5,000+", icon: Users, color: "text-nexus-teal", bg: "bg-nexus-teal/10" },
  { key: "discussions", value: "1,200+", icon: MessageSquareText, color: "text-nexus-yellow", bg: "bg-nexus-yellow/10" },
  { key: "mentors", value: "250+", icon: GraduationCap, color: "text-nexus-red", bg: "bg-nexus-red/10" },
  { key: "jobs", value: "300+", icon: Briefcase, color: "text-purple-600", bg: "bg-purple-100 dark:bg-purple-950/40" },
] as const;

const Community = () => {
  const { t, language } = useLanguage();
  const isRTL = language === "ar";
  const arrowClass = `ms-2 h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 ${isRTL ? "rotate-180" : ""}`;

  return (
    <Layout>
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 pt-24 pb-16 dark:from-nexus-dark dark:to-nexus-dark-light">
        <div className="pointer-events-none absolute -top-24 -start-24 h-72 w-72 rounded-full bg-nexus-teal/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -end-24 h-72 w-72 rounded-full bg-nexus-yellow/20 blur-3xl" />
        <div className="container relative">
          <div className="mx-auto max-w-3xl animate-fade-in text-center">
            <Badge className="mb-4 bg-nexus-yellow/20 text-nexus-gray hover:bg-nexus-yellow/30">{t("community_hero.badge")}</Badge>
            <h1 className="mb-6 text-4xl font-bold text-nexus-gray dark:text-nexus-light md:text-5xl">{t("community_hero.title")}</h1>
            <p className="mb-8 text-xl text-gray-600 dark:text-gray-300">{t("community_hero.description")}</p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button asChild className="group bg-nexus-brand text-white hover:bg-nexus-brand/90">
                <a href={PLATFORM_ROUTES.signup} target="_blank" rel="noopener noreferrer">
                  {t("community_hero.createProfile")}<ArrowRight className={arrowClass} />
                </a>
              </Button>
              <Button asChild variant="outline" className="border-2 border-nexus-gray text-nexus-gray dark:text-nexus-light">
                <a href={PLATFORM_ROUTES.community} target="_blank" rel="noopener noreferrer">{t("community_hero.exploreCommunity")}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 dark:bg-nexus-dark">
        <div className="container">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-nexus-gray dark:text-nexus-light">{t("features.title")}</h2>
            <p className="text-gray-600 dark:text-gray-300">{t("features.description")}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featureModules.map((feature) => (
              <Card key={feature.key} className="group h-full border-none shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full transition-transform group-hover:scale-110 ${feature.bg}`}>
                    <feature.icon className={`h-6 w-6 ${feature.color}`} />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-nexus-gray dark:text-nexus-light">{t(`features.modules.${feature.key}.title`)}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{t(`features.modules.${feature.key}.description`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-16 dark:bg-nexus-dark-light">
        <div className="container">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-nexus-gray dark:text-nexus-light">{t("deepDive.title")}</h2>
            <p className="text-gray-600 dark:text-gray-300">{t("deepDive.description")}</p>
          </div>
          <Tabs defaultValue="profiles" className="mx-auto max-w-5xl" dir={isRTL ? "rtl" : "ltr"}>
            <TabsList className="mb-8 grid h-auto grid-cols-2 md:grid-cols-4" dir="ltr">
              {(isRTL ? [...deepDives].reverse() : deepDives).map((tab) => <TabsTrigger key={tab.key} value={tab.key} className="py-2" dir={isRTL ? "rtl" : "ltr"}>{t(`deepDive.tabs.${tab.key}.label`)}</TabsTrigger>)}
            </TabsList>
            {deepDives.map((tab) => (
              <TabsContent key={tab.key} value={tab.key} className="animate-fade-in rounded-lg border bg-white p-6 dark:bg-nexus-dark">
                <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2" dir="ltr">
                  <div className={isRTL ? "md:order-2" : "md:order-1"} dir={isRTL ? "rtl" : "ltr"}>
                    <h3 className="mb-4 text-2xl font-semibold text-nexus-gray dark:text-nexus-light">{t(`deepDive.tabs.${tab.key}.title`)}</h3>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">{t(`deepDive.tabs.${tab.key}.description`)}</p>
                    <ul className="mb-6 space-y-3">
                      {(t(`deepDive.tabs.${tab.key}.points`) as string[]).map((point) => (
                        <li key={point} className="flex items-start gap-3"><CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-nexus-teal" /><span className="text-gray-600 dark:text-gray-300">{point}</span></li>
                      ))}
                    </ul>
                    <Button asChild className="group bg-nexus-brand hover:bg-nexus-brand/90">
                      <a href={tab.href} target="_blank" rel="noopener noreferrer">{t(`deepDive.tabs.${tab.key}.cta`)}<ArrowRight className={arrowClass} /></a>
                    </Button>
                  </div>
                  <div className={`relative flex h-64 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-nexus-teal/10 via-white to-nexus-yellow/10 dark:via-nexus-dark ${isRTL ? "md:order-1" : "md:order-2"}`}>
                    <tab.icon className="h-20 w-20 text-nexus-teal/60 animate-bounce-slow" />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Community statistics are hidden for the first release.
          Restore this section once real community usage numbers are available.
      <section className="bg-white py-16 dark:bg-nexus-dark">
        <div className="container grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.key} className="border-none shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full ${stat.bg}`}><stat.icon className={`h-6 w-6 ${stat.color}`} /></div>
                <div className="text-3xl font-bold text-nexus-gray dark:text-nexus-light">{stat.value}</div>
                <p className="text-gray-500 dark:text-gray-300">{t(`stats.${stat.key}`)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
      */}

      <section className="bg-gradient-to-r from-nexus-teal/20 to-nexus-yellow/20 py-16">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-white animate-bounce-slow"><Sparkles className="h-8 w-8 text-nexus-yellow" /></div>
          <h2 className="mb-4 text-3xl font-bold text-nexus-gray dark:text-nexus-light">{t("joinCTA.title")}</h2>
          <p className="mb-8 text-gray-600 dark:text-gray-300">{t("joinCTA.description")}</p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button asChild className="group bg-nexus-brand px-8 py-6 text-lg text-white hover:bg-nexus-brand/90">
              <a href={PLATFORM_ROUTES.signup} target="_blank" rel="noopener noreferrer">{t("joinCTA.action")}<ArrowRight className={arrowClass} /></a>
            </Button>
            <Button asChild variant="outline" className="border-2 border-nexus-gray px-8 py-6 text-lg text-nexus-gray dark:text-nexus-light">
              <a href={PLATFORM_ROUTES.mentorship} target="_blank" rel="noopener noreferrer">{t("joinCTA.mentorAction")}</a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Community;
